import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateCompanyQueries } from "@/lib/companyQueries";

/**
 * Save a single field on a company via PATCH /api/companies/:id.
 * - Optimistically updates the ["company", id] cache
 * - Rolls back on failure
 * - Toast + invalidate on success
 */
export function useCompanyField(companyId: string | undefined) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: unknown }) => {
      if (!companyId) throw new Error("Missing companyId");
      const res = await fetch(`/api/companies/${companyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save (HTTP ${res.status})`);
      }
      return res.json();
    },
    onMutate: async ({ field, value }) => {
      if (!companyId) return;
      await queryClient.cancelQueries({ queryKey: ["company", companyId] });
      const previous = queryClient.getQueryData<Record<string, unknown>>(["company", companyId]);
      if (previous) {
        queryClient.setQueryData(["company", companyId], { ...previous, [field]: value });
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous && companyId) {
        queryClient.setQueryData(["company", companyId], context.previous);
      }
      toast.error(err instanceof Error ? err.message : "Failed to save");
    },
    onSuccess: () => {
      toast.success("Saved");
    },
    onSettled: () => {
      if (companyId) {
        return invalidateCompanyQueries(queryClient, companyId);
      }
    },
  });

  const saveField = (field: string, value: unknown) => mutation.mutate({ field, value });
  return { saveField, saveFieldAsync: (field: string, value: unknown) => mutation.mutateAsync({ field, value }), isSaving: mutation.isPending };
}
